/**
 * What a household member sees after they have answered (#1343).
 *
 * ⛔ THIS EXISTS BECAUSE THE ACCOUNT HOLDER IS OFTEN NOT THE COOK. The link used
 * to end at "Thanks, Sam" — a dead end for the one person who had just proved
 * they would participate, and no way for whoever is actually at the stove to
 * read the recipe.
 *
 * ⚠️ No account, no password. The link is still the credential, and it now slides
 * its own expiry forward each time it is used, so the person cooking on a
 * Wednesday is not locked out by a token issued on a Sunday.
 *
 * ⚠️ Everyone's allergies and medical restrictions are shown. A household knows
 * this about itself, and the person cooking needs it more than anybody.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

interface Restriction { target: string; severity: string; note: string | null }
interface KitchenEater { name: string; restrictions: Restriction[] }
interface KitchenSlot {
  dayIndex: number;
  slot: string;
  mealId: number | null;
  mealName: string | null;
  source: string;
  sides: string[];
  startTime: string | null;
  note: string | null;
}
interface KitchenItem {
  id: number;
  itemName: string;
  aisle: string;
  qtyNote: string | null;
  checked: boolean;
  haveAlready: boolean;
  forMeals: string[];
}
interface Kitchen {
  you: { name: string };
  household: { name: string };
  eaters: KitchenEater[];
  plan: { id: number; shopDate: string; slots: KitchenSlot[]; items: KitchenItem[] } | null;
}

const SEVERITY_WORD: Record<string, string> = {
  allergy: "allergic to",
  medical: "must not have",
  avoid: "does not eat",
};

export function MemberKitchen({ token }: { token: string }) {
  const [openMeal, setOpenMeal] = useState<number | null>(null);
  const q = useQuery<Kitchen>({
    queryKey: ["member-kitchen", token],
    queryFn: () => api(`/link/${token}/kitchen`, { auth: false }),
  });

  if (q.isLoading) return <p className="text-muted mt-6 text-sm">Loading the kitchen…</p>;
  if (q.isError || !q.data) return null;

  const { plan, eaters } = q.data;
  const days = plan ? [...new Set(plan.slots.map((s) => s.dayIndex))].sort((a, b) => a - b) : [];
  // Only restrictions worth reading aloud. An empty list should say nothing.
  const withRestrictions = eaters.filter((e) => e.restrictions.length > 0);

  return (
    <div className="mt-8">
      {!plan && (
        <p className="text-muted text-sm">
          Nothing is planned yet. When it is, it shows up here — no login needed.
        </p>
      )}

      {plan && (
        <>
          <h2 className="text-muted text-xs uppercase tracking-wide">What is for dinner</h2>
          <div className="mt-2 flex flex-col gap-2">
            {days.map((d) => {
              const dinner = plan.slots.find((s) => s.dayIndex === d && s.slot === "dinner");
              if (!dinner) return null;
              return (
                <div key={d} className="border-line rounded-lg border p-3">
                  <p className="text-muted text-xs">Day {d + 1}</p>
                  <p className="mt-0.5 font-medium">{dinner.mealName ?? "Not decided"}</p>
                  {dinner.sides.length > 0 && (
                    <p className="text-muted mt-0.5 text-sm">with {dinner.sides.join(", ")}</p>
                  )}
                  {dinner.startTime && (
                    <p className="text-plate-a mt-1 text-xs">Start it at {dinner.startTime}</p>
                  )}
                  {dinner.mealId && (
                    <button
                      onClick={() => setOpenMeal(openMeal === dinner.mealId ? null : dinner.mealId)}
                      className="text-plate-b mt-2 text-sm underline underline-offset-4"
                    >
                      {openMeal === dinner.mealId ? "Hide the recipe" : "How to make it"}
                    </button>
                  )}
                  {openMeal === dinner.mealId && dinner.mealId && (
                    <Method token={token} mealId={dinner.mealId} />
                  )}
                </div>
              );
            })}
          </div>

          <ShoppingList token={token} items={plan.items} />
        </>
      )}

      <ClaimAccount token={token} />

      {withRestrictions.length > 0 && (
        <>
          <h2 className="text-muted mt-8 text-xs uppercase tracking-wide">
            Before you cook
          </h2>
          <div
            className="mt-2 rounded-lg border p-3"
            style={{ borderColor: "var(--color-warn)" }}
          >
            {withRestrictions.map((e) => (
              <p key={e.name} className="text-sm">
                <strong>{e.name}</strong> is{" "}
                {e.restrictions
                  .map((r) => `${SEVERITY_WORD[r.severity] ?? r.severity} ${r.target}`)
                  .join(", ")}
                .
              </p>
            ))}
            <p className="text-muted mt-2 text-xs">
              Meals are already planned around this. It is here so the person at the
              stove knows too.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Turn the link into a login of your own (#1344).
 *
 * ⚠️ Offered AFTER the kitchen, never before it. Somebody who just wants to know
 * what is for dinner already has everything they need from the link; asking them
 * to make an account first is the friction the invite flow exists to avoid.
 *
 * ⛔ Signing in is a separate step by design — the account is created wherever
 * accounts are created, and the token is what says which household it belongs
 * to. An account alone proves nothing about which household you are in.
 */
function ClaimAccount({ token }: { token: string }) {
  const [state, setState] = useState<"idle" | "done" | "error">("idle");
  const [message, setMessage] = useState("");

  const claim = useMutation({
    mutationFn: () =>
      api(`/members/claim`, { method: "POST", body: JSON.stringify({ token }) }),
    onSuccess: () => {
      setState("done");
      setMessage("Done. You can sign in with your own account from now on.");
    },
    onError: () => {
      setState("error");
      // The honest instruction, not a shrug. The commonest cause is simply not
      // being signed in yet.
      setMessage("Sign in or make an account first, then come back to this link and tap again.");
    },
  });

  return (
    <div className="border-line mt-8 rounded-lg border p-3">
      <p className="text-sm font-medium">Want your own login?</p>
      <p className="text-muted mt-1 text-sm">
        You do not need one — this link keeps working. But an account means you
        can get back in from any device without hunting for it.
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-3">
        <a
          href="/sign-in"
          className="border-line rounded-lg border px-3 py-1.5 text-sm font-medium"
        >
          Sign in
        </a>
        <button
          onClick={() => claim.mutate()}
          disabled={claim.isPending || state === "done"}
          className="bg-plate-b text-ground rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
        >
          {claim.isPending ? "Linking…" : state === "done" ? "Linked" : "Link this household"}
        </button>
      </div>
      {message && (
        <p className={state === "error" ? "text-warn mt-2 text-sm" : "text-muted mt-2 text-sm"}>
          {message}
        </p>
      )}
    </div>
  );
}

function Method({ token, mealId }: { token: string; mealId: number }) {
  const q = useQuery<{ method: { steps: { text: string; ingredients: string[] }[]; gather: any; seasoning: Record<string, string> } }>({
    queryKey: ["member-method", token, mealId],
    queryFn: () => api(`/link/${token}/meals/${mealId}/method`, { auth: false }),
  });

  if (q.isLoading) return <p className="text-muted mt-2 text-sm">Loading…</p>;
  if (q.isError || !q.data) return <p className="text-warn mt-2 text-sm">Could not load that recipe.</p>;

  const { steps, gather } = q.data.method;

  return (
    <div className="border-line mt-3 rounded-lg border p-3">
      {gather?.base?.length > 0 && (
        <>
          <p className="text-muted text-xs uppercase tracking-wide">Get this out</p>
          <ul className="mt-1.5 flex flex-col gap-1 text-sm">
            {gather.base.map((l: any) => (
              <li key={l.item} className="break-words">
                {l.amount && <span className="font-semibold">{l.amount} </span>}
                {l.item}
              </li>
            ))}
          </ul>
          {gather.forks?.map((f: any) => (
            <div key={f.group} className="mt-2">
              <p className="text-plate-b text-xs uppercase tracking-wide">
                Only for {f.forPlates.join(" and ")}
              </p>
              <ul className="mt-1 flex flex-col gap-1 text-sm">
                {f.lines.map((l: any) => (
                  <li key={l.item} className="break-words">
                    {l.amount && <span className="font-semibold">{l.amount} </span>}
                    {l.item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}

      <p className="text-muted mt-3 text-xs uppercase tracking-wide">Method</p>
      <ol className="mt-1.5 flex list-decimal flex-col gap-2 pl-5 text-sm">
        {steps.map((s, i) => (
          <li key={i} className="break-words">
            {s.text}
            {s.ingredients.length > 0 && (
              <span className="text-muted block text-xs">{s.ingredients.join(" · ")}</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function ShoppingList({ token, items }: { token: string; items: KitchenItem[] }) {
  const qc = useQueryClient();
  const tick = useMutation({
    mutationFn: ({ id, checked }: { id: number; checked: boolean }) =>
      api(`/link/${token}/list/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ checked }),
        auth: false,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["member-kitchen", token] }),
  });

  if (items.length === 0) return null;

  return (
    <>
      <h2 className="text-muted mt-8 text-xs uppercase tracking-wide">
        The shopping list
      </h2>
      {/* ⚠️ Ticking works from here on purpose — the person holding this link is
          very often the one actually in the shop. */}
      <div className="mt-2 flex flex-col gap-1">
        {items.map((i) => (
          <label key={i.id} className="border-line flex items-start gap-2.5 rounded-lg border p-2.5">
            <input
              type="checkbox"
              checked={i.checked}
              onChange={(e) => tick.mutate({ id: i.id, checked: e.target.checked })}
              className="mt-1"
            />
            <span className={i.checked ? "text-muted line-through" : ""}>
              {i.qtyNote && <span className="font-semibold">{i.qtyNote} </span>}
              {i.itemName}
              {i.haveAlready && <span className="text-muted text-xs"> · already have</span>}
            </span>
          </label>
        ))}
      </div>
    </>
  );
}
